import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Upload, Video, FileType, Users, Eye, CheckCircle, AlertCircle, AlertTriangle, Plus, User, Mail, Phone } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import FileUploadDropzone from '@/components/common/FileUploadDropzone';
import { HiveShareService } from '@/services/hiveShareService';
import { useToast } from '@/components/ui/use-toast';
import { 
  debouncedFamilyNameCheck, 
  debouncedParentEmailCheck,
  ValidationResult,
  FamilyDuplicate,
  ParentDuplicate 
} from '@/services/familyValidationService';
import { 
  FamilyDuplicateResolutionDialog, 
  ParentDuplicateResolutionDialog 
} from './DuplicateResolutionDialog';

// Validation schema
const videoUploadSchema = z.object({
  title: z.string().min(1, 'Title is required').max(100, 'Title must be less than 100 characters'),
  description: z.string().optional(),
  tags: z.string().optional(),
  privacy_level: z.enum(['organization', 'selected_families', 'create_new_family']),
  accessible_family_ids: z.array(z.string()).optional(),
  // New family creation fields
  new_family_name: z.string().optional(),
  parent_first_name: z.string().optional(),
  parent_last_name: z.string().optional(),
  parent_email: z.string().optional(),
  parent_phone: z.string().optional(),
}).refine((data) => {
  // If creating a new family, require family name and parent details
  if (data.privacy_level === 'create_new_family') {
    return !!(
      data.new_family_name?.trim() && 
      data.parent_first_name?.trim() && 
      data.parent_last_name?.trim() &&
      data.parent_email?.trim()
    );
  }
  return true;
}, {
  message: 'Family name, parent first name, last name, and email are required when creating a new family',
  path: ['new_family_name'], // This will show the error on the family name field
});

type VideoUploadFormData = z.infer<typeof videoUploadSchema>;

interface VideoUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  families?: Array<{ id: string; name: string }>;
  onSuccess?: (videoId: string) => void;
}

interface UploadProgress {
  stage: 'selecting' | 'uploading' | 'processing' | 'complete' | 'error';
  progress: number;
  message: string;
}

export const VideoUploadDialog: React.FC<VideoUploadDialogProps> = ({
  open,
  onOpenChange,
  organizationId,
  families = [],
  onSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    stage: 'selecting',
    progress: 0,
    message: 'Ready to upload'
  });
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  // Validation states
  const [familyValidation, setFamilyValidation] = useState<ValidationResult | null>(null);
  const [parentEmailValidation, setParentEmailValidation] = useState<ValidationResult | null>(null);
  const [isValidatingFamily, setIsValidatingFamily] = useState(false);
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);

  // Duplicate resolution dialogs
  const [showFamilyDuplicateDialog, setShowFamilyDuplicateDialog] = useState(false);
  const [showParentDuplicateDialog, setShowParentDuplicateDialog] = useState(false);

  const form = useForm<VideoUploadFormData>({
    resolver: zodResolver(videoUploadSchema),
    defaultValues: {
      title: '',
      description: '',
      tags: '',
      privacy_level: 'organization',
      accessible_family_ids: [],
      // New family creation defaults
      new_family_name: '',
      parent_first_name: '',
      parent_last_name: '',
      parent_email: '',
      parent_phone: '',
    },
  });

  const privacyLevel = form.watch('privacy_level');
  const familyName = form.watch('new_family_name');
  const parentEmail = form.watch('parent_email');

  // Auto-populate parent last name from family name (exact pattern from AddParentDialog)
  React.useEffect(() => {
    console.log('Auto-populate effect triggered:', { open, privacyLevel, familyName }); // Debug log
    if (open && privacyLevel === 'create_new_family' && familyName) {
      console.log('Setting parent_last_name to:', familyName); // Debug log
      form.setValue('parent_last_name', familyName);
    }
  }, [open, privacyLevel, familyName, form]);

  // Family name validation function (called onBlur)
  const validateFamilyName = async (name: string) => {
    if (!name || !name.trim()) {
      setFamilyValidation(null);
      return;
    }
    
    setIsValidatingFamily(true);
    try {
      const result = await debouncedFamilyNameCheck(name.trim(), organizationId);
      setFamilyValidation(result);
    } catch (error) {
      console.error('Family name validation error:', error);
    } finally {
      setIsValidatingFamily(false);
    }
  };

  // Parent email validation function (called onBlur)
  const validateParentEmail = async (email: string) => {
    if (!email || !email.trim()) {
      setParentEmailValidation(null);
      return;
    }
    
    setIsValidatingEmail(true);
    try {
      const result = await debouncedParentEmailCheck(email.trim(), organizationId);
      setParentEmailValidation(result);
    } catch (error) {
      console.error('Parent email validation error:', error);
    } finally {
      setIsValidatingEmail(false);
    }
  };

  // Duplicate resolution handlers
  const handleFamilyDuplicateResolution = {
    useExisting: (familyId: string) => {
      // Switch to selected families mode and select the existing family
      form.setValue('privacy_level', 'selected_families');
      form.setValue('accessible_family_ids', [familyId]);
      setShowFamilyDuplicateDialog(false);
    },
    createWithNewName: (newName: string) => {
      form.setValue('new_family_name', newName);
      setShowFamilyDuplicateDialog(false);
    },
    continueAnyway: () => {
      setShowFamilyDuplicateDialog(false);
    }
  };

  const handleParentDuplicateResolution = {
    addToExistingFamily: (familyId: string) => {
      // Switch to selected families mode and select the existing family
      form.setValue('privacy_level', 'selected_families');
      form.setValue('accessible_family_ids', [familyId]);
      setShowParentDuplicateDialog(false);
    },
    useDifferentEmail: (newEmail: string) => {
      form.setValue('parent_email', newEmail);
      setShowParentDuplicateDialog(false);
    },
    continueAnyway: () => {
      setShowParentDuplicateDialog(false);
    }
  };

  // Check for duplicates before upload
  const checkForDuplicatesBeforeUpload = async (data: VideoUploadFormData): Promise<boolean> => {
    if (data.privacy_level !== 'create_new_family') {
      return true; // No need to check if not creating new family
    }

    // Check family name duplicates
    if (familyValidation && !familyValidation.isValid) {
      setShowFamilyDuplicateDialog(true);
      return false;
    }

    // Check parent email duplicates
    if (parentEmailValidation && !parentEmailValidation.isValid) {
      setShowParentDuplicateDialog(true);
      return false;
    }

    return true;
  };

  // Handle file selection
  const handleFileSelect = (files: File[]) => {
    if (files.length > 0) {
      const file = files[0];
      setSelectedFile(file);
      
      // Auto-populate title from filename if empty
      if (!form.getValues('title')) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        form.setValue('title', nameWithoutExt);
      }
      
      setUploadProgress({
        stage: 'selecting',
        progress: 0,
        message: `Selected: ${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} MB)`
      });
    }
  };

  // Handle upload
  const handleUpload = async (data: VideoUploadFormData) => {
    if (!selectedFile) {
      toast({
        title: 'No file selected',
        description: 'Please select a video file to upload.',
        variant: 'destructive',
      });
      return;
    }

    // Check for duplicates before proceeding
    const canProceed = await checkForDuplicatesBeforeUpload(data);
    if (!canProceed) {
      return; // Duplicate resolution dialog will be shown
    }

    setIsUploading(true);
    setUploadProgress({
      stage: 'uploading',
      progress: 10,
      message: 'Preparing upload...'
    });

    try {
      // Parse tags if provided
      const tags = data.tags ? data.tags.split(',').map(tag => tag.trim()).filter(Boolean) : [];
      
      // Create upload request
      const uploadRequest = {
        title: data.title,
        description: data.description,
        tags,
        privacy_level: data.privacy_level,
        accessible_family_ids: data.privacy_level === 'selected_families' ? data.accessible_family_ids : [],
        // Include new family creation fields when creating a new family
        ...(data.privacy_level === 'create_new_family' && {
          new_family_name: data.new_family_name,
          parent_first_name: data.parent_first_name,
          parent_last_name: data.parent_last_name,
          parent_email: data.parent_email,
          parent_phone: data.parent_phone,
        }),
      };

      setUploadProgress({
        stage: 'uploading',
        progress: 30,
        message: 'Getting upload URL...'
      });

      // Get upload URL from HiveShare service
      const response = await HiveShareService.createVideoUpload(
        organizationId,
        uploadRequest,
        selectedFile
      );

      if (!response.success || !response.upload_url) {
        throw new Error(response.error || 'Failed to get upload URL');
      }

      setUploadProgress({
        stage: 'uploading',
        progress: 50,
        message: 'Uploading video to HiveShare...'
      });

      // Upload file directly to HiveShare
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);

      const uploadResponse = await fetch(response.upload_url, {
        method: 'POST',
        body: uploadFormData,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload video to HiveShare');
      }

      setUploadProgress({
        stage: 'processing',
        progress: 80,
        message: 'Video uploaded successfully! Processing...'
      });

      // Wait a moment for processing to begin
      setTimeout(() => {
        setUploadProgress({
          stage: 'complete',
          progress: 100,
          message: 'Upload complete! Video is being processed and will be available shortly.'
        });

        toast({
          title: 'Video uploaded successfully!',
          description: 'Your video is being processed and will be available to view soon.',
        });

        onSuccess?.(response.video_id!);
        
        // Reset form and close dialog after a short delay
        setTimeout(() => {
          handleClose();
        }, 2000);
      }, 1000);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadProgress({
        stage: 'error',
        progress: 0,
        message: error instanceof Error ? error.message : 'Upload failed'
      });

      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Failed to upload video',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      // Reset form state
      console.log('Form reset called in handleClose'); // Debug log
      form.reset();
      setSelectedFile(null);
      setUploadProgress({
        stage: 'selecting',
        progress: 0,
        message: 'Ready to upload'
      });
      setIsUploading(false);
      onOpenChange(false);
    }
  };

  const getProgressVariant = () => {
    switch (uploadProgress.stage) {
      case 'complete':
        return 'default';
      case 'error':
        return 'destructive';
      default:
        return 'default';
    }
  };

  const getStageIcon = () => {
    switch (uploadProgress.stage) {
      case 'uploading':
      case 'processing':
        return <Upload className="h-4 w-4 animate-pulse text-blue-600" />;
      case 'complete':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Video className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5 text-blue-600" />
            Upload Video to HiveShare
          </DialogTitle>
          <DialogDescription>
            Share videos with families in your organization. Videos are securely hosted and can be accessed by parents.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh] pr-4">
          <div className="space-y-6">
            {/* Upload Progress */}
            {(isUploading || uploadProgress.stage !== 'selecting') && (
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-3">
                    {getStageIcon()}
                    <span className="text-sm font-medium">{uploadProgress.message}</span>
                  </div>
                  <Progress 
                    value={uploadProgress.progress} 
                    className="w-full"
                  />
                </CardContent>
              </Card>
            )}

            {/* File Upload Section */}
            {uploadProgress.stage === 'selecting' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Select Video File</h3>
                  <FileUploadDropzone
                    onFilesSelected={handleFileSelect}
                    maxFiles={1}
                    maxSize={500 * 1024 * 1024} // 500MB max
                    accept={{
                      'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm']
                    }}
                    disabled={isUploading}
                    className="w-full"
                  />
                </div>

                {selectedFile && (
                  <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <FileType className="h-8 w-8 text-blue-600 mt-1 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</p>
                          <div className="flex flex-wrap gap-2 mt-1">
                            <Badge variant="secondary" className="text-xs">
                              {(selectedFile.size / (1024 * 1024)).toFixed(1)} MB
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                              {selectedFile.type || 'Unknown format'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Video Details Form */}
            {selectedFile && uploadProgress.stage === 'selecting' && (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleUpload)} className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-gray-900">Video Details</h3>
                    
                    {/* Title */}
                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Title</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter video title" {...field} />
                          </FormControl>
                          <FormDescription>
                            A clear, descriptive title for your video
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Description */}
                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description (Optional)</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Describe what this video contains..."
                              className="min-h-[80px] resize-none"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            Help families understand what the video is about
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Tags */}
                    <FormField
                      control={form.control}
                      name="tags"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tags (Optional)</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="recital, performance, class, rehearsal"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            Comma-separated tags to help organize videos
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Separator />

                    {/* Privacy Settings */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        <Eye className="h-4 w-4" />
                        Privacy & Access
                      </h3>

                      <FormField
                        control={form.control}
                        name="privacy_level"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Who can view this video?</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select privacy level" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="organization">
                                  <div className="flex items-center gap-2">
                                    <Users className="h-4 w-4" />
                                    <span>All families in organization</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="selected_families">
                                  <div className="flex items-center gap-2">
                                    <Eye className="h-4 w-4" />
                                    <span>Selected families only</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="create_new_family">
                                  <div className="flex items-center gap-2">
                                    <Plus className="h-4 w-4" />
                                    <span>Create new family</span>
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              {privacyLevel === 'organization' 
                                ? 'All families will be able to view this video'
                                : privacyLevel === 'selected_families'
                                ? 'Only families you select will be able to view this video'
                                : 'Create a new family and invite the parent to view this video'
                              }
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Family Selection */}
                      {privacyLevel === 'selected_families' && families.length > 0 && (
                        <FormField
                          control={form.control}
                          name="accessible_family_ids"
                          render={() => (
                            <FormItem>
                              <FormLabel>Select Families</FormLabel>
                              <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-3">
                                {families.map((family) => (
                                  <FormField
                                    key={family.id}
                                    control={form.control}
                                    name="accessible_family_ids"
                                    render={({ field }) => {
                                      return (
                                        <FormItem
                                          key={family.id}
                                          className="flex flex-row items-start space-x-3 space-y-0"
                                        >
                                          <FormControl>
                                            <Checkbox
                                              checked={field.value?.includes(family.id)}
                                              onCheckedChange={(checked) => {
                                                return checked
                                                  ? field.onChange([...field.value, family.id])
                                                  : field.onChange(
                                                      field.value?.filter(
                                                        (value) => value !== family.id
                                                      )
                                                    )
                                              }}
                                            />
                                          </FormControl>
                                          <FormLabel className="font-normal text-sm cursor-pointer">
                                            {family.name}
                                          </FormLabel>
                                        </FormItem>
                                      )
                                    }}
                                  />
                                ))}
                              </div>
                              <FormDescription>
                                Select which families can access this video
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}

                      {/* New Family Creation Form */}
                      {privacyLevel === 'create_new_family' && (
                        <div className="space-y-4 mt-6 p-4 border rounded-lg bg-blue-50">
                          <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                            <Plus className="h-4 w-4 text-blue-600" />
                            Create New Family
                          </h4>
                          
                          {/* Family Name */}
                          <FormField
                            control={form.control}
                            name="new_family_name"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Family Name</FormLabel>
                                <FormControl>
                                  <div className="relative">
                                    <Input 
                                      placeholder="e.g., Smith Family"
                                      {...field} 
                                      onBlur={(e) => {
                                        field.onBlur(e); // Call the original onBlur
                                        validateFamilyName(e.target.value); // Validate on blur
                                      }}
                                      className={
                                        familyValidation && !familyValidation.isValid
                                          ? 'border-amber-300 focus:border-amber-500'
                                          : familyValidation && familyValidation.isValid
                                          ? 'border-green-300 focus:border-green-500'
                                          : ''
                                      }
                                    />
                                    {isValidatingFamily && (
                                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
                                      </div>
                                    )}
                                    {!isValidatingFamily && familyValidation && familyValidation.isValid && (
                                      <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-600" />
                                    )}
                                    {!isValidatingFamily && familyValidation && !familyValidation.isValid && (
                                      <AlertTriangle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-amber-600" />
                                    )}
                                  </div>
                                </FormControl>
                                {familyValidation && !familyValidation.isValid && (
                                  <div className="text-xs text-amber-600 mt-1">
                                    ⚠️ Similar family exists: {(familyValidation.duplicates as FamilyDuplicate[])[0]?.name}
                                  </div>
                                )}
                                {familyValidation && familyValidation.suggestions && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    Similar: {familyValidation.suggestions.join(', ')}
                                  </div>
                                )}
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Parent First Name */}
                            <FormField
                              control={form.control}
                              name="parent_first_name"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Parent First Name</FormLabel>
                                  <FormControl>
                                    <Input 
                                      placeholder="John"
                                      {...field} 
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            {/* Parent Last Name */}
                            <FormField
                              control={form.control}
                              name="parent_last_name"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Parent Last Name</FormLabel>
                                  <FormControl>
                                    <Input 
                                      placeholder="Smith"
                                      {...field} 
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Parent Email */}
                            <FormField
                              control={form.control}
                              name="parent_email"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Parent Email</FormLabel>
                                  <FormControl>
                                    <div className="relative">
                                      <Input 
                                        type="email"
                                        placeholder="john.smith@email.com"
                                        {...field} 
                                        onBlur={(e) => {
                                          field.onBlur(e); // Call the original onBlur
                                          validateParentEmail(e.target.value); // Validate on blur
                                        }}
                                        className={
                                          parentEmailValidation && !parentEmailValidation.isValid
                                            ? 'border-amber-300 focus:border-amber-500'
                                            : parentEmailValidation && parentEmailValidation.isValid
                                            ? 'border-green-300 focus:border-green-500'
                                            : ''
                                        }
                                      />
                                      {isValidatingEmail && (
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
                                        </div>
                                      )}
                                      {!isValidatingEmail && parentEmailValidation && parentEmailValidation.isValid && (
                                        <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-600" />
                                      )}
                                      {!isValidatingEmail && parentEmailValidation && !parentEmailValidation.isValid && (
                                        <AlertTriangle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-amber-600" />
                                      )}
                                    </div>
                                  </FormControl>
                                  <FormDescription className="text-xs">
                                    Required for video access and invitations
                                  </FormDescription>
                                  {parentEmailValidation && !parentEmailValidation.isValid && (
                                    <div className="text-xs text-amber-600 mt-1">
                                      ⚠️ Email exists in: {(parentEmailValidation.duplicates as ParentDuplicate[])[0]?.family_name}
                                    </div>
                                  )}
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            {/* Parent Phone */}
                            <FormField
                              control={form.control}
                              name="parent_phone"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Parent Phone (Optional)</FormLabel>
                                  <FormControl>
                                    <Input 
                                      type="tel"
                                      placeholder="(555) 123-4567"
                                      {...field} 
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          <div className="flex items-start gap-2 p-3 bg-blue-100 rounded-md">
                            <Mail className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="text-sm text-blue-700">
                              <p className="font-medium">What happens next:</p>
                              <ul className="mt-1 space-y-1 text-xs">
                                <li>• Family will be created automatically</li>
                                <li>• Parent will receive an email invitation</li>
                                <li>• Video will be shared with the new family</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={handleClose}
                      disabled={isUploading}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={isUploading || !selectedFile}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {isUploading ? (
                        <>
                          <Upload className="h-4 w-4 mr-2 animate-pulse" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload Video
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            )}
          </div>
        </ScrollArea>

        {/* Duplicate Resolution Dialogs */}
        <FamilyDuplicateResolutionDialog
          open={showFamilyDuplicateDialog}
          onOpenChange={setShowFamilyDuplicateDialog}
          familyName={familyName || ''}
          duplicates={familyValidation?.duplicates as FamilyDuplicate[] || []}
          suggestions={familyValidation?.suggestions}
          onUseExisting={handleFamilyDuplicateResolution.useExisting}
          onCreateWithNewName={handleFamilyDuplicateResolution.createWithNewName}
          onContinueAnyway={handleFamilyDuplicateResolution.continueAnyway}
        />

        <ParentDuplicateResolutionDialog
          open={showParentDuplicateDialog}
          onOpenChange={setShowParentDuplicateDialog}
          parentEmail={parentEmail || ''}
          duplicates={parentEmailValidation?.duplicates as ParentDuplicate[] || []}
          onAddToExistingFamily={handleParentDuplicateResolution.addToExistingFamily}
          onUseDifferentEmail={handleParentDuplicateResolution.useDifferentEmail}
          onContinueAnyway={handleParentDuplicateResolution.continueAnyway}
        />
      </DialogContent>
    </Dialog>
  );
};

export default VideoUploadDialog;